import React from 'react';
import { connect } from 'react-redux';
import Bugsnag from '@bugsnag/js';
import PropTypes from 'prop-types';

import ErrorPage from '../../pages/ErrorPage';
import { createErrorToast } from '../../redux/modules/app';
import ChildrenType from '../../types/ChildrenType';
import { bugsnagDiscardedMessages } from '../../utils/bugsnagUtil';
import { queryClient } from '../../utils/queryClientUtil';

/**
 * ErrorBoundary Component
 *
 * This is used when you want more granular error handling for a specific page or component.
 * Simply wrap your component with the ErrorBoundary and you can handle errors in variety of
 * different ways.
 *
 * Current supported:
 *
 *  fallbackComponent - pass this prop to render a different component on error, defaults to ErrorPage
 *  showErrorToast - set false to prevent the error toast from showing the error
 *
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error) {
    const { dispatch, showErrorToast } = this.props;

    const message = error?.message
      || error?.error?.message
      || (typeof error === 'string' ? error : '');

    if (!bugsnagDiscardedMessages.some(m => message.includes(m))) {
      Bugsnag.notify(error);
    }

    if (showErrorToast) {
      dispatch(createErrorToast({ title: error.message, error }));
    }
  }

  render() {
    const { children, fallbackComponent, httpError, ...props } = this.props;
    const { error } = this.state;
    const Fallback = fallbackComponent;

    if (httpError && httpError.status === 401) {
      setTimeout(() => {
        queryClient.clear();
        window.location = '/logout';
      }, 2000);

      return <Fallback {...props} error={httpError} />;
    }

    if (error) {
      return <Fallback {...props} error={error} />;
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: ChildrenType.isRequired,
  dispatch: PropTypes.func.isRequired,
  fallbackComponent: PropTypes.func,
  showErrorToast: PropTypes.bool
};

ErrorBoundary.defaultProps = {
  fallbackComponent: props => <ErrorPage {...props} />,
  showErrorToast: true
};

const mapStateToProps = ({ error }) => ({ httpError: error });

export default connect(mapStateToProps)(ErrorBoundary);
